<script lang="ts" setup>
import Modal from '~/components/Modal.vue'
import Button from '~/components/Button.vue'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'
import type { AllPackageInterface } from '~/types/shipzeePro/packagesTypes'
import { useStoreConfig } from '~/store/storeConfig'

const { t } = useLang()
const router = useRouter()
const { isMobile } = useStoreConfig()

const props = defineProps<{
  visible: boolean
  shipment?: AllPackageInterface
}>()
const emit = defineEmits(['handle', 'loadFrData'])

const visible = ref<Boolean>(props.visible)

watch(
  () => props.visible,
  (isVisible) => {
    visible.value = isVisible
  }
)

function handle(value: boolean) {
  emit('handle', value)
}

function redirectToTracking(tracking: { number: string | null }) {
  const trackingRoute = getLocalizedRoute(Routes.TrackingId, { id: tracking.number })
  const trackingUrl = new URL(router.resolve(trackingRoute).href, window.location.href).href

  return window.open(trackingUrl, '_blank')
}

const items = computed(() =>
  props.shipment?.fr_data?.packages?.map((item) => ({
    label: item.id,
    data: item,
    defaultOpen: false,
  }))
)
</script>

<template>
  <div>
    <Modal :visible="visible" :header="t('order.tracking.shipments_tracking')" size-xl @handle="handle">
      <div class="content">
        <div class="shipments-table flex flex-col rounded-sm overflow-y-auto">
          <div class="table-heading flex py-5 px-8 gap-x-1">
            <div class="basis-1/2">{{ t('order.tracking.courier_title') }}</div>
            <div class="basis-1/2 text-right">{{ t('order.tracking.tracking_title') }}</div>
          </div>
          <UAccordion
            :items="items"
            :ui="{
              item: {
                padding: 'px-5',
              },
            }"
            multiple>
            <template #default="{ item, open }">
              <div class="flex w-full p-4 items-center gap-x-1" :class="[open && '!text-brand-primary']">
                <div class="basis-1/2">{{ t('couriers.aec') }}</div>
                <div class="flex basis-1/2 text-text2 dark:text-dmText2 justify-end">
                  <div
                    class="hover:underline text-right cursor-pointer flex items-center mr-4"
                    @click.stop="redirectToTracking({ number: item.data.aec_tracking })">
                    <span>{{ item.data.aec_tracking }}</span>
                    <i class="fa fa-search text-brand-primary ml-1"></i>
                  </div>
                  <div class="body-item-5">
                    <div
                      class="toggle ml-auto flex items-center justify-around w-10 h-10 bg-neutral7 dark:bg-dmGray3 rounded-full cursor-pointer">
                      <i
                        class="fa-solid"
                        :class="{
                          'fa-chevron-up text-brand-primary': open,
                          'fa-chevron-down': !open,
                        }" />
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template #item="{ item }">
              <div
                v-for="(whItem, itemIndex) in item.data.warehouse_items"
                :key="itemIndex"
                class="mb-4 w-full lg:pb-4 lg:border-b border-neutral5 last-of-type:pb-0 last-of-type:border-0">
                <LazyMobileAllShipmentsTableRowPackages
                  v-if="isMobile"
                  :key="`${itemIndex}-mobile`"
                  :item="whItem"
                  :shipment="shipment" />
                <LazyAllShipmentsTableRowPackages
                  v-else
                  :key="itemIndex"
                  :item="whItem"
                  :shipment="shipment"
                  :is-modal="true"
                  :opened-packages="[props.shipment]" />
              </div>
            </template>
          </UAccordion>
        </div>
        <Button class="w-full mt-4" @click="visible = false">
          {{ t('common.close') }}
        </Button>
      </div>
    </Modal>
  </div>
</template>

<style lang="postcss" scoped>
.content {
  @apply flex flex-col;

  .shipments-table {
    @apply mb-5;

    & > div {
      @apply border-l border-r border-b border-neutral7 dark:border-dmGray4;
    }
    & > div:first-child {
      @apply rounded-t-md border-t;
    }
    & > div:last-child {
      @apply rounded-b-md;
    }
  }

  .table-heading {
    @apply bg-white dark:bg-dmGray1;
  }

  :deep(.collapsible) {
    .collapsible-image {
      @apply flex w-20 h-20 min-w-[5rem] min-h-[5rem] rounded-[0.5rem];
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center center;
    }

    .collapsible-body {
      .content > div:last-of-type {
        margin-bottom: 0 !important;
      }
    }

    .collapsible-header {
      @apply bg-white dark:bg-dmGray1 rounded-none;
    }

    &.-active {
      .collapsible-body {
        @apply lg:bg-neutral7 rounded-none;
      }
    }

    .collapsible-body > .content {
      @apply p-0 md:p-4;
    }

    &:last-child {
      @apply overflow-hidden;

      .collapsible-body {
        @apply rounded-bl-[1rem] rounded-br-[1rem] overflow-hidden;
      }

      &:not(.-active) {
        .collapsible-header {
          @apply rounded-bl-[1rem] rounded-br-[1rem];
        }
      }
    }

    .icon {
      display: none;
    }
  }

  .divider {
    @apply h-[1px] w-full bg-neutral7 my-3;
  }
}
</style>

<style lang="postcss" scoped>
.wl {
  .toggle {
    @apply bg-brand-primaryLight;
  }
}

.ae .content {
  .shipments-table {
    .table-heading {
      @apply bg-lighterYellow;
    }
  }
}
</style>
